import classNames from 'classnames';
import { MomentInput } from 'moment';
import { ComponentProps, useCallback } from 'react';

import Emoji from './Emoji';
import FillPill from './FillPill';
import ProgressBackground from './ProgressBackground';
import TimeRange from './TimeRange';
import moment from 'moment-timezone';
import { FavouriteIcon } from '@tymbe/ui-icons';
import useSearchString from '../../../hooks/UrlParamsHandler/useSearchParams';

type CalendarItemOwnProps = {
  emoji?: string | null;
  start: MomentInput;
  end: MomentInput;
  currentApplications: number;
  totalApplications: number;
  hasUnconfirmedAttendance?: boolean;
  pendingInvitationsCount?: number;
  className?: string;
  shiftId?: number;
};
type CalendarItemProps = CalendarItemOwnProps &
  Omit<ComponentProps<'div'>, keyof CalendarItemOwnProps>;

const CalendarItem = ({
  emoji,
  start,
  end,
  currentApplications,
  totalApplications,
  hasUnconfirmedAttendance,
  pendingInvitationsCount = 0,
  className = '',
  shiftId,
  ...rest
}: CalendarItemProps) => {
  const progress = currentApplications / totalApplications;
  const now = moment();
  const startTime = moment(start);
  const [searchParams, setSearchParams] = useSearchString();
  const selectedShiftId = searchParams.get('shiftId') || undefined;

  const started = startTime.isBefore(now);

  const notFilledApplications =
    totalApplications - currentApplications - pendingInvitationsCount;

  const onAttendanceClick = useCallback(() => {
    const newShiftId = shiftId;
    const newParams = new URLSearchParams(searchParams);
    if (!newShiftId) {
      newParams.delete('shiftAttendance');
    } else {
      newParams.set('shiftAttendance', `${newShiftId}`);
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams, shiftId]);

  const onItemClick = useCallback(() => {
    if (started) {
      onAttendanceClick();
    } else {
      const newShiftId = shiftId;
      const newParams = new URLSearchParams(searchParams);
      if (!newShiftId) {
        newParams.delete('shiftId');
      } else {
        newParams.set('shiftId', `${newShiftId}`);
      }
      setSearchParams(newParams);
    }
  }, [
    started,
    onAttendanceClick,
    searchParams,
    setSearchParams,
    shiftId,
  ]);

  return (
    <div>
        <FavouriteIcon
          name="error"
          className={classNames("h-4 w-4 absolute z-40 -top-1 -right-1 text-red-500 cursor-pointer",
            { hidden: !started || !hasUnconfirmedAttendance },
          )}
        />
      <div
        onClick={onItemClick}
        className={classNames(
          'relative text-caption-xl font-semibold rounded-md border overflow-hidden bg-white hover:shadow-down-md',
          {
            'text-secondary-600 border-primary-400 shadow-down-xs':
              (currentApplications === totalApplications) && !started,
          },
          {
            'text-secondary-600 border-secondary-transparent-10 hover:border-secondary-200 hover:bg-secondary-150 shadow-down-xs':
              (currentApplications >= 0) &&
              (currentApplications < totalApplications) &&
              !started,
          },
          { 'text-secondary-400 border-secondary-transparent-10': started },
          {
            'outline-secondary-600 outline outline-4':
              shiftId === Number(selectedShiftId),
          },
          className
        )}
        {...rest}
      >
        <ProgressBackground
          progress={progress}
          className={classNames(
            'z-0',
            {
              'bg-blue-100':
                currentApplications < totalApplications && !started,
            },
            {
              'bg-primary-200':
                currentApplications === totalApplications && !started,
            },
            { 'bg-white': started }
          )}
        />
        <div className="relative p-1 flex items-center gap-2 w-full">
          <Emoji
            className={classNames(
              'bg-secondary-transparent-5 size-10 text-[22px] leading-[22px]',
              { 'opacity-50': started }
            )}
            emoji={emoji}
          />
          <div className="grow">
            <TimeRange start={start} end={end} />
            <div className="relative">
              <FillPill
                current={currentApplications}
                total={totalApplications}
                notFilledApplications={notFilledApplications}
                finished={started}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarItem;
